import ToolBar from '@/components/ToolBar';

import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import AlertBox from '@/components/AlertBox';
import { useContext, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { getPhoneNumberValue } from '@/components/FormFieldPhone';
import { genUniqueId } from '@/lib/utils';
import { uploadMedia } from '@/services/uploadMedia';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import Stepper from '@/components/Stepper';
import { DocumentType, vendorContactDetailsSchema, vendorDetailsSchema } from './vendorSchema';
import { useCreateVendorMutation } from '@/services/vendorService';
import { VendorDetailsStep } from './VendorDetailsStep';
import { ContactForm } from '@/components/ContactForm';
import type { GenericErrorResponse } from '@/types/api.types';

const schemas = [vendorDetailsSchema, vendorContactDetailsSchema];

export const VendorCreate = () => {
  const [isAlertBoxOpen, setIsAlertBoxOpen] = useState(false);
  const [searchParam, setSearchParam] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [currentSchema, setCurrentSchema] = useState(schemas[activeStep]);
  const [documents, setDocuments] = useState<DocumentType[]>([
    { id: genUniqueId(), name: '', document: null, mediaId: null },
  ]);
  const createVendorMutation = useCreateVendorMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const form = useForm<
    z.infer<typeof vendorDetailsSchema> & {
      contacts?: z.infer<typeof vendorContactDetailsSchema>['contacts'];
    }
  >({
    resolver: zodResolver(currentSchema),
    mode: 'onBlur',
  });
  const {
    trigger,
    clearErrors,
    formState: { isDirty, isValid },
  } = form;
  const [isDocumentUploadLoading, setIsDocumentUploadLoading] = useState(false);
  const [steps, setSteps] = useState([
    { title: 'Vendor Details', isCompleted: false },
    { title: 'Contact Details', isCompleted: false },
  ]);

  const { setIsDirty } = useContext(NavigationBlockerContext);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  useEffect(() => {
    setSearchParam({ stepper: steps[0].title }, { replace: true });
  }, []);

  useEffect(() => {
    const activeStep = searchParam.get('stepper');
    if (activeStep) {
      const index = steps.findIndex((step) => step.title === activeStep);
      setActiveStep(index);
    }
  }, [searchParam]);

  const scrollToTop = () => {
    const toolbarElement = document.getElementById('toolbar');
    if (toolbarElement) {
      toolbarElement.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setCurrentSchema(schemas[activeStep]);
    setTimeout(async () => {
      await trigger();
      clearErrors();
    }, 50);
    setTimeout(() => {
      scrollToTop();
    }, 100);
  }, [activeStep]);

  const goToStep = async (step: number) => {
    const isValid = await trigger();
    if (isValid) {
      if (steps[step].isCompleted || step <= activeStep) {
        setSearchParam({ stepper: steps[step].title });
        setActiveStep(step);
      }
    }
  };
  const nextStep = async () => {
    const isValid = await trigger();
    if (isValid) {
      setSteps((prevSteps) =>
        prevSteps.map((step, index) => (index === activeStep ? { ...step, isCompleted: true } : step))
      );
      setSearchParam({ stepper: steps[activeStep + 1].title });
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    setSearchParam({ stepper: steps[activeStep - 1].title });
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    const docData = [];
    if (documents.filter((doc) => doc.document).length) {
      setIsDocumentUploadLoading(true);
      const updatedDocuments = await Promise.all(
        documents.map(async (doc) => {
          if (doc.document && !doc.mediaId) {
            const media = await uploadMedia(doc.document, 'VENDOR');
            return { ...doc, mediaId: media?.mediaId };
          }
          return doc;
        })
      );
      setDocuments(updatedDocuments);
      setIsDocumentUploadLoading(false);
      if (updatedDocuments.some((doc) => !doc.mediaId)) return;
      for (const doc of updatedDocuments) {
        if (doc.mediaId) {
          docData.push({
            name: doc.name,
            mediaId: doc.mediaId,
          });
        }
      }
    }

    const data = form.getValues();

    createVendorMutation.mutate(
      {
        ...data,
        coverageAreas: data.coverageAreas
          .split(',')
          .map((area) => area.trim())
          .filter((area) => area),
        counties: data.counties ?? [],
        fees: Number(data.fees),
        phone: getPhoneNumberValue(data.phone)!,
        documents: docData ?? [],
        contacts: data.contacts
          ?.map((contact) => {
            const hasFirstName = contact.firstName?.trim();
            const hasLastName = contact.lastName?.trim();
            if (hasFirstName && hasLastName) {
              return {
                firstName: contact.firstName!,
                lastName: contact.lastName!,
                email: contact.email!,
                phone: getPhoneNumberValue(contact.phone)!,
              };
            }
            return null;
          })
          .filter((contact) => contact !== null),
      },
      {
        onSuccess: (data) => {
          setIsAlertBoxOpen(false);
          navigate('/vendors');
          toast({
            variant: 'default',
            description: data.message,
          });
        },
        onError: (e: GenericErrorResponse) => {
          setIsAlertBoxOpen(false);
          displayError(e, 5000);
        },
      }
    );
  };
  return (
    <>
      <div className="w-full scrollbar-hide" id="stepper">
        <ToolBar title="Add New Vendor" />
        <div className="mt-3 flex justify-center pl-24">
          <Stepper steps={steps} activeStep={activeStep} goToStep={goToStep} />
        </div>

        <div className="flex flex-col gap-6">
          <FormProvider {...form}>
            {activeStep === 0 && <VendorDetailsStep mode="CREATE" documents={documents} setDocuments={setDocuments} />}
            {activeStep === 1 && <ContactForm mode="create" isFetching={isDocumentUploadLoading} contactFor="VENDOR" />}
          </FormProvider>
          <div className={`flex flex-row justify-end gap-6 pb-8`}>
            {activeStep !== 0 && (
              <Button
                variant="outline"
                onClick={prevStep}
                type="button"
                className=" w-[75px] h-[36px] focus:outline-none"
              >
                Back
              </Button>
            )}

            {activeStep !== steps.length - 1 && (
              <Button
                variant="default"
                className=" h-[36px] w-[103px] hover:bg-[#12375C]"
                onClick={nextStep}
                disabled={activeStep === steps.length - 1 || !form.formState.isValid}
              >
                Next
              </Button>
            )}
            {activeStep === steps.length - 1 && (
              <AlertBox
                triggerButton={
                  <Button
                    variant="default"
                    className=" h-[36px] w-[103px] hover:bg-[#12375C]"
                    disabled={!isValid}
                    data-testid="trigger-save-button"
                    onClick={() => setIsAlertBoxOpen(true)}
                  >
                    Save
                  </Button>
                }
                title={`Do you want to save the record?`}
                description={`This action will create a new vendor record in the database.`}
                cancelBtnLabel="Cancel"
                confirmBtnLabel="Save"
                open={isAlertBoxOpen}
                isLoading={isDocumentUploadLoading || createVendorMutation.isPending}
                cancelBtnClick={() => setIsAlertBoxOpen(false)}
                confirmBtnClick={handleSubmit}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
